/**
 * The FAQ is stored as markdown and edited as rich text.
 *
 * ── Why the storage format cannot change ──────────────────────────────────
 *
 * `## ` is not decoration: it is ai_bot's RETRIEVAL UNIT. `splitMarkdownByH2`
 * cuts the document at every `## ` line and each piece becomes one embedded
 * chunk, which is the thing the bot searches and then quotes back to a
 * customer verbatim. Storing HTML would mean rewriting that splitter, the
 * duplicate-heading check, the publish audit, the spreadsheet import and the
 * verbatim-answer path — all of which read `## `.
 *
 * So the editor speaks HTML and the database keeps markdown, and these two
 * functions are the join. Nothing downstream of the save knows the editor
 * changed.
 *
 * ── The one thing that does not survive the round trip ────────────────────
 *
 * Whether a heading is followed by a blank line. The real documents are split
 * 575 / 333 on this and HTML has no way to carry the difference — `## H\nText`
 * and `## H\n\nText` are both `<h2>H</h2><p>Text</p>`.
 *
 * That is safe, and provably so rather than hopefully: `splitMarkdownByH2`
 * ends with `.trim()` on the section content, so both forms produce the
 * IDENTICAL chunk. The bot cannot tell them apart. `scripts/kb_roundtrip.mjs`
 * checks exactly that against every real document rather than taking it on
 * trust — it compares the SECTIONS, not the characters.
 *
 * ── Deliberately small ────────────────────────────────────────────────────
 *
 * Headings, paragraphs, bullets, bold, links. Nothing else, because nothing
 * else is in the data (measured: 908 `##`, 447 `- `, 3 `#`, and zero bold,
 * links, tables, HTML or code across every document) and because every
 * construct admitted here is another thing that can round-trip wrongly and
 * silently corrupt a brand's FAQ. The editor's toolbar is restricted to
 * match — see faqEditor.vue — so the editor cannot produce what this cannot
 * carry.
 */

/**
 * A bullet line. `-` ALONE counts: one real document uses it as a spacer, and
 * treating it as a paragraph puts a blank line either side of it on save.
 */
const RE_BULLET = /^-(?:\s+|$)/

/** Characters that must not reach `innerHTML` as themselves. */
function esc(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function unesc(s) {
  return String(s)
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // Last, or an escaped "&amp;lt;" would decode twice.
    .replace(/&amp;/g, '&')
}

/** http(s) only. A `javascript:` href typed into the link dialog is dropped. */
function safeHref(url) {
  return /^https?:\/\//i.test(String(url).trim()) ? String(url).trim() : null
}

/**
 * Inline markdown → inline HTML. Escape FIRST: everything after this point
 * adds tags, so a `<` from the author is already inert.
 */
function inlineToHtml(text) {
  let out = esc(text)

  // **bold** — non-greedy, no newline inside, so `a ** b\nc ** d` is left alone.
  out = out.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>')

  // [label](url)
  out = out.replace(/\[([^\]\n]*)\]\(([^)\s]+)\)/g, (m, label, url) => {
    const href = safeHref(unesc(url))
    return href ? `<a href="${esc(href)}">${label || esc(href)}</a>` : m
  })

  // Bare URLs, but not one already inside the href or label of the above.
  out = out.replace(/(^|[\s(])(https?:\/\/[^\s<]+)/g, (m, pre, url) => {
    return `${pre}<a href="${esc(url)}">${esc(unesc(url))}</a>`
  })

  return out
}

/** Inline HTML → inline markdown. */
function inlineToMd(html) {
  let out = String(html)

  // A link whose text IS its href goes back as a bare URL, not `[u](u)`.
  out = out.replace(/<a\b[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, (m, href, label) => {
    const plainLabel = unesc(label.replace(/<[^>]+>/g, '')).trim()
    const plainHref = unesc(href).trim()
    if (!plainHref) return plainLabel
    return plainLabel === plainHref ? plainHref : `[${plainLabel}](${plainHref})`
  })

  out = out.replace(/<(strong|b)\b[^>]*>([\s\S]*?)<\/\1>/gi, (m, tag, inner) => {
    const t = inner.replace(/<[^>]+>/g, '')
    // `** **` is not bold in any renderer, and emitting it would corrupt the
    // text around it. An empty bold is simply dropped.
    return t.trim() ? `**${t}**` : t
  })

  // A soft break inside a block is a real newline in the stored text.
  out = out.replace(/<br\s*\/?>/gi, '\n')

  // Anything else the editor managed to emit is stripped rather than stored:
  // storing an unknown tag would put raw HTML in front of a customer.
  out = out.replace(/<[^>]+>/g, '')

  return unesc(out)
}

/**
 * markdown → HTML for the editor.
 *
 * Block rules, in order of precedence per line:
 *   `# x`   → h1      (a document title; three exist in the real data)
 *   `## x`  → h2      (the retrieval unit)
 *   `- x`   → li, consecutive ones in one ul
 *   blank   → block separator
 *   other   → p, consecutive lines joined with <br>
 */
export function mdToHtml(md) {
  const lines = String(md == null ? '' : md).split('\n')
  const out = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (/^\s*$/.test(line)) {
      i++
      continue
    }

    let m = line.match(/^#\s+(.*)$/)
    if (m) {
      out.push(`<h1>${inlineToHtml(m[1])}</h1>`)
      i++
      continue
    }

    m = line.match(/^##\s+(.*)$/)
    if (m) {
      out.push(`<h2>${inlineToHtml(m[1])}</h2>`)
      i++
      continue
    }

    if (RE_BULLET.test(line)) {
      const items = []
      while (i < lines.length && RE_BULLET.test(lines[i])) {
        items.push(`<li>${inlineToHtml(lines[i].replace(RE_BULLET, ''))}</li>`)
        i++
      }
      out.push(`<ul>${items.join('')}</ul>`)
      continue
    }

    // A run of ordinary lines is ONE paragraph with soft breaks. Making each
    // line its own paragraph would turn every single newline into a blank
    // line on the next save.
    const para = []
    while (
      i < lines.length &&
      !/^\s*$/.test(lines[i]) &&
      !/^#{1,2}\s+/.test(lines[i]) &&
      !RE_BULLET.test(lines[i])
    ) {
      para.push(inlineToHtml(lines[i]))
      i++
    }
    out.push(`<p>${para.join('<br>')}</p>`)
  }

  return out.join('')
}

/**
 * HTML from the editor → markdown for storage.
 *
 * Only the five blocks mdToHtml can produce are recognised. Anything else is
 * flattened to a paragraph rather than dropped — losing an author's text is
 * worse than losing its formatting.
 */
export function htmlToMd(html) {
  const src = String(html == null ? '' : html)
  const blocks = []
  const re = /<(h1|h2|p|ul|ol)\b[^>]*>([\s\S]*?)<\/\1>/gi
  let m
  let lastEnd = 0

  while ((m = re.exec(src)) !== null) {
    // Text sitting between blocks (CKEditor does not produce this, but a
    // paste or a future plugin might) is kept as its own paragraph.
    const between = src.slice(lastEnd, m.index)
    const loose = inlineToMd(between).trim()
    if (loose) blocks.push({ kind: 'p', text: loose })
    lastEnd = re.lastIndex

    const tag = m[1].toLowerCase()
    const inner = m[2]

    if (tag === 'h1') {
      const t = inlineToMd(inner).trim()
      if (t) blocks.push({ kind: 'h', text: `# ${t}` })
    } else if (tag === 'h2') {
      const t = inlineToMd(inner).trim()
      if (t) blocks.push({ kind: 'h', text: `## ${t}` })
    } else if (tag === 'ul' || tag === 'ol') {
      const items = []
      const li = /<li\b[^>]*>([\s\S]*?)<\/li>/gi
      let l
      while ((l = li.exec(inner)) !== null) {
        // An EMPTY item is kept as a bare `-`. One real document uses it as a
        // spacer between blocks, and dropping it closes a gap the author put
        // there on purpose.
        const t = inlineToMd(l[1]).trim()
        items.push(t ? `- ${t}` : '-')
      }
      if (items.length) blocks.push({ kind: 'list', text: items.join('\n') })
    } else {
      // `<p>&nbsp;</p>` is how the editor spells a deliberately empty line;
      // it carries nothing, and keeping it would grow the document by one
      // blank line on every save.
      // Trailing spaces are NOT stripped: the stored text is quoted to a
      // customer verbatim, and "fish. \nAvoid" losing its space is a change
      // to an answer nobody asked for.
      const t = inlineToMd(inner).trim()
      if (t) blocks.push({ kind: 'p', text: t })
    }
  }

  const tail = inlineToMd(src.slice(lastEnd)).trim()
  if (tail) blocks.push({ kind: 'p', text: tail })

  /*
   * Blocks are separated by a blank line — EXCEPT either side of a list,
   * which hugs the text around it with a single newline.
   *
   * Not a style choice. Counted across every real document: 68 lists follow
   * their lead-in on the next line against 2 with a blank line between, and
   * all 50 paragraphs that follow a list do so on the next line. Joining
   * everything with a blank line would insert one into 118 places a person
   * wrote deliberately, and the bot quotes this text to a customer verbatim.
   */
  let out = ''
  for (let k = 0; k < blocks.length; k++) {
    if (k > 0) {
      const hugs = blocks[k].kind === 'list' || blocks[k - 1].kind === 'list'
      out += hugs ? '\n' : '\n\n'
    }
    out += blocks[k].text
  }
  return out
}

/**
 * The markdown a document would be stored as after a round trip through the
 * editor, without changing it.
 *
 * Opening a document and saving it unchanged must not rewrite it — the change
 * log would show a body edit nobody made. faqEditor compares this against the
 * live editor content and sends the ORIGINAL text when they match.
 */
export function normaliseMd(md) {
  return htmlToMd(mdToHtml(md))
}


/* ─────────────────────────────────────────────────────────────────────────
 * Splitting a document into the questions it is made of.
 *
 * The editor edits ONE answer at a time, but the database still stores one
 * markdown document, so these two have to be exact inverses:
 *
 *     joinDoc(splitDoc(md)) === md      for any md, byte for byte
 *
 * That is why a part keeps its RAW text rather than a parsed title and body.
 * Rebuilding a section from its pieces would re-impose one separator style on
 * a document that uses several (we measured: 575 headings followed by a blank
 * line, 333 not), and every untouched question in the file would be rewritten
 * the first time somebody edited a different one.
 *
 * So: untouched parts are copied through verbatim, and only the part somebody
 * actually edited is regenerated. `scripts/kb_roundtrip.mjs` asserts the
 * identity above against every real document.
 * ───────────────────────────────────────────────────────────────────────── */

/**
 * Cut a document at its `## ` lines.
 *
 * Returns `[{ kind, title, body, raw }]` where `raw` is the exact slice of
 * the original. The first part is the preamble — everything before the first
 * heading, which is where a `# Title` lives — and is present only when the
 * document has one.
 */
export function splitDoc(md) {
  const text = String(md == null ? '' : md)
  const lines = text.split('\n')
  const parts = []
  let buf = []
  let title = null

  const flush = () => {
    if (title === null && !buf.length) return
    const raw = buf.join('\n')
    if (title === null) {
      // Whitespace-only preamble is not a part; it belongs to the next one.
      if (!raw.trim()) {
        if (raw) parts.push({ kind: 'preamble', title: '', body: raw, raw })
        return
      }
      parts.push({ kind: 'preamble', title: '', body: raw, raw })
      return
    }
    /*
     * `sep` is the whitespace between the heading line and the answer, and it
     * is kept because the documents are not consistent about it — 575 of 908
     * headings are followed by a blank line and 333 are not. Rebuilding with
     * one fixed separator would change the other style every time somebody
     * edited that question. `body` therefore excludes it, so the editor shows
     * the answer and not a leading empty line.
     */
    const rest = buf.slice(1).join('\n')
    const lead = (rest.match(/^\n*/) || [''])[0]
    parts.push({
      kind: 'section',
      title,
      sep: '\n' + lead,
      body: rest.slice(lead.length),
      raw,
    })
  }

  for (const line of lines) {
    const m = line.match(/^##\s+(.+)$/)
    if (m) {
      flush()
      title = m[1].trim()
      buf = [line]
    } else {
      buf.push(line)
    }
  }
  flush()
  return parts
}

/** The exact inverse of splitDoc. */
export function joinDoc(parts) {
  return (parts || []).map((p) => p.raw).join('\n')
}

/**
 * Replace one part's answer, keeping everything else byte-identical.
 *
 * `title` and `body` are what the author typed; the trailing blank lines of
 * the part being replaced are carried over, because they are the separator
 * to whatever comes next and dropping them would close a gap the author did
 * not touch.
 */
export function replacePart(parts, index, { title, body }) {
  const out = parts.slice()
  const part = out[index]
  if (!part) return out

  const trailing = (part.raw.match(/\n*$/) || [''])[0]
  const answer = String(body == null ? '' : body).replace(/\n+$/, '')

  if (part.kind === 'preamble') {
    out[index] = { ...part, body: answer, raw: answer + trailing }
    return out
  }

  const heading = `## ${String(title == null ? '' : title).trim()}`
  // The separator this question already used, so editing one answer does not
  // restyle the whitespace of a question nobody touched.
  const sep = part.sep || '\n'
  const raw = (answer ? `${heading}${sep}${answer}` : heading) + trailing
  out[index] = { ...part, title: String(title || '').trim(), body: answer, raw }
  return out
}

/*
 * ── The primary-answer marker ──────────────────────────────────────────
 *
 * A question can be declared the answer to one or more exact words:
 *
 *   ## How do I deposit? <!-- quick: deposit, 存款, simpan -->
 *
 * A player who sends exactly "deposit" gets that answer back, instead of
 * being asked what they wanted. ai_bot lifts it into the chunk at publish
 * (core/kb_store.ts) and matches it at runtime (core/kb_quick_answer.ts).
 *
 * It lives inside the heading because the heading is the only per-question
 * thing the markdown can carry, and the markdown IS the document: one source
 * of truth, versioned and audited with the rest of the FAQ. The author never
 * types the comment — the editor reads it out of the heading into its own
 * field and writes it back on save.
 */
const RE_QUICK = /<!--\s*quick:\s*([^>]*?)\s*-->/i

/** Split a raw heading into the question the author sees and its trigger words. */
export function readQuickTriggers(rawTitle) {
  const title = String(rawTitle == null ? '' : rawTitle)
  const m = title.match(RE_QUICK)
  const triggers = (m ? m[1] : '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
  return { text: title.replace(RE_QUICK, '').replace(/\s+/g, ' ').trim(), triggers }
}

/**
 * Put them back. An empty list removes the marker entirely, so unticking the
 * box leaves the heading exactly as it would have been written by hand.
 */
export function writeQuickTriggers(text, triggers) {
  const clean = (triggers || [])
    .map((t) => String(t).trim())
    .filter(Boolean)
    // Deduplicated case-insensitively: ai_bot normalizes before it matches,
    // so two spellings of one word are one trigger there anyway.
    .filter((t, i, all) => all.findIndex((o) => o.toLowerCase() === t.toLowerCase()) === i)
  const base = String(text == null ? '' : text)
    .replace(RE_QUICK, '')
    .replace(/\s+/g, ' ')
    .trim()
  return clean.length ? `${base} <!-- quick: ${clean.join(', ')} -->` : base
}

export const kbMarkdownHelper = {
  mdToHtml,
  htmlToMd,
  normaliseMd,
  splitDoc,
  joinDoc,
  replacePart,
  readQuickTriggers,
  writeQuickTriggers,
}
